import { type FC, memo, useCallback, useState } from 'react';
import { useCompletion } from 'ai/react';
import { ArrowUp } from 'lucide-react';
import { useEditor } from 'novel';
import { addAIHighlight } from 'novel/extensions';
import Markdown from 'react-markdown';
import { toast } from 'react-toastify';

import { Command, CommandInput } from '../../ui/command';
import { Button } from '../../ui/button';
import CrazySpinner from '../../ui/icons/crazy-spinner';
import Magic from '../../ui/icons/magic';
import { ScrollArea } from '../../ui/scroll-area';

import AICompletionCommands from './aiCompletionCommand';
import AISelectorCommands from './aiSelectorCommands';

export interface AISelectorProps {
  onOpenChange: (open: boolean) => void;
}

const AISelector: FC<AISelectorProps> = (props) => {
  const { onOpenChange } = props;
  const { editor } = useEditor();
  const [inputValue, setInputValue] = useState('');
  const { completion, complete, isLoading } = useCompletion({
    api: `${(process.env.PROTECTED_API_URL || '/api')?.replace(/^\/$/, '')}/generate`,
    onError: (e) => {
      toast.error(e.message);
    },
  });

  const hasCompletion = completion.length > 0;

  const handleClick = useCallback(() => {
    if (!editor) {
      return undefined;
    }
    if (completion) {
      return complete(completion, {
        body: {
          option: 'zap',
          command: inputValue,
        },
      }).then(() => setInputValue(''));
    }

    const slice = editor.state.selection.content();
    const text = editor.storage.markdown.serializer.serialize(slice.content);

    complete(text, {
      body: { option: 'zap', command: inputValue },
    }).then(() => setInputValue(''));
    return undefined;
  }, [completion, complete, isLoading]);

  if (!editor) {
    return null;
  }

  return (
    <Command className="w-[350px]">
      {hasCompletion && (
        <div className="flex max-h-[400px]">
          <ScrollArea>
            <div className="prose p-2 px-4 prose-sm">
              <Markdown>{completion}</Markdown>
            </div>
          </ScrollArea>
        </div>
      )}

      {isLoading && (
        <div className="flex h-12 w-full items-center px-4 text-sm font-medium text-muted-foreground text-blue-500">
          <Magic className="mr-2 h-4 w-4 shrink-0  " />
          AI is thinking
          <div className="ml-2 mt-1">
            <CrazySpinner />
          </div>
        </div>
      )}
      {!isLoading && (
        <>
          <div className="relative">
            <CommandInput
              value={inputValue}
              onValueChange={setInputValue}
              autoFocus
              placeholder={hasCompletion ? 'Tell AI what to do next' : 'Ask AI to edit or generate...'}
              onFocus={() => addAIHighlight(editor)}
            />
            <Button
              size="icon"
              className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-blue-500 hover:bg-blue-900"
              onClick={handleClick}
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
          {hasCompletion && (
            <AICompletionCommands
              onDiscard={() => {
                editor.chain().unsetHighlight().focus().run();
                onOpenChange(false);
              }}
              completion={completion}
            />
          )}
          {!hasCompletion && <AISelectorCommands onSelect={(value, option) => complete(value, { body: { option } })} />}
        </>
      )}
    </Command>
  );
};

export default memo(AISelector);
